import { Select as SelectPrimitive } from "@base-ui/react/select";
import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { triggerControlStyles } from "@/components/ui/control-styles";
import { cn } from "@/lib/utils";

export type SelectOption = { label: string; value: string; disabled?: boolean };

function optionText(node: React.ReactNode): string {
  return React.Children.toArray(node)
    .map((part) => {
      if (typeof part === "string" || typeof part === "number") return String(part);
      if (React.isValidElement<{ children?: React.ReactNode }>(part)) {
        return optionText(part.props.children);
      }
      return "";
    })
    .join("");
}

export function Select({
  options,
  value,
  onValueChange,
  placeholder,
  className,
  disabled,
  id,
  children,
  onChange,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: {
  options?: readonly SelectOption[];
  value: string;
  onValueChange?: (value: string) => void;
  onChange?: React.ChangeEventHandler<HTMLSelectElement>;
  children?: React.ReactNode;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const fallbackOptions = React.Children.toArray(children).flatMap((child) => {
    if (
      !React.isValidElement<{
        value?: string;
        disabled?: boolean;
        children?: React.ReactNode;
      }>(child)
    )
      return [];
    return [
      {
        value: child.props.value ?? "",
        label: optionText(child.props.children),
        disabled: child.props.disabled,
      },
    ];
  });
  const normalizedOptions = options ?? fallbackOptions;
  const selectedLabel = normalizedOptions.find(
    (option) => option.value === value,
  )?.label;
  const change = (next: string | null) => {
    const nextValue = next ?? "";
    onValueChange?.(nextValue);
    onChange?.({
      target: { value: nextValue },
    } as React.ChangeEvent<HTMLSelectElement>);
  };
  return (
    <SelectPrimitive.Root
      value={value || null}
      onValueChange={change}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-full px-4 text-sm text-ink pointer-coarse:h-11",
          triggerControlStyles,
          className,
        )}
      >
        <SelectPrimitive.Value
          className="min-w-0 truncate text-left"
          placeholder={
            placeholder ?? normalizedOptions[0]?.label ?? "Select an option"
          }
        >
          {selectedLabel}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon className="ml-2 shrink-0 text-muted">
          <ChevronDown className="size-3.5" aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Positioner
          alignItemWithTrigger={false}
          sideOffset={6}
          className="z-50"
        >
          <SelectPrimitive.Popup className="max-h-[min(18rem,var(--available-height))] min-w-(--anchor-width) overflow-y-auto rounded-md border border-line bg-panel p-1 shadow-panel outline-hidden transition-all duration-150 data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 motion-reduce:transform-none motion-reduce:transition-none">
            <SelectPrimitive.List>
              {normalizedOptions.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className="grid min-h-9 cursor-default grid-cols-[1rem_minmax(0,1fr)] items-center gap-2 rounded-sm px-2 py-2 text-sm text-ink data-highlighted:bg-panel-raised data-highlighted:font-medium data-highlighted:outline-solid data-highlighted:outline-2 data-highlighted:outline-focus data-highlighted:-outline-offset-2 forced-colors:data-highlighted:outline-[Highlight] data-disabled:pointer-events-none data-disabled:opacity-50 pointer-coarse:min-h-11"
                >
                  <SelectPrimitive.ItemIndicator>
                    <Check className="size-3.5" aria-hidden="true" />
                  </SelectPrimitive.ItemIndicator>
                  <SelectPrimitive.ItemText className="col-start-2 min-w-0 truncate">
                    {option.label}
                  </SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.List>
          </SelectPrimitive.Popup>
        </SelectPrimitive.Positioner>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
